import React, { useContext } from "react";
import moment from "moment";
import preval from "preval.macro";
import { IMachineCenterLogsProps } from "./MachineCenterLogsInterfaces";
import { MachineCenterLogsContext } from "./MachineCenterLogsContext";
import { getCurrentOperator, getMachineCenterIdHMI, isUptimeActive } from "utilities/storageUtilities";
import { getLastInternetConnectionRecoveryDate } from "utilities/storageUtilities";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { getDeviceOperativeSystem, getDeviceType, truncateLongText } from "utilities/utilities";

const buildDateString: string = preval`module.exports = (new Date()).toISOString();`;
const buildDateTimeStamp = moment(buildDateString);
const deviceOperativeSystem = getDeviceOperativeSystem();
const deviceType = getDeviceType();

export const MachineCenterLogsProvider: React.FC<IMachineCenterLogsProps> = ({ children }) => {
    const { store } = useContext(HMICachedDataContext);
    const { failureDetailsStep } = useContext(HMIContext);
    const internetConnectionRecoveryDate = getLastInternetConnectionRecoveryDate();
    const { insertHMILog } = MachineCentersEntity.InsertMachineCenterLog();
    const uptime = isUptimeActive();
    const localTimezone = moment.tz.guess();

    const currentMomentWithTimezone = moment.tz(localTimezone);

    const triggerMachineCenterLogs = async (
        trigger: string | undefined,
        message: string | undefined,
        lastInternetConnection?: string,
    ) => {
        const currentOperator = getCurrentOperator();
        const machineCenter = getMachineCenterIdHMI();

        const machineCenterLogInfo = {
            machine_center_id: store.machineCenter?.machine_center_id ?? machineCenter?.machine_center_id,
            local_timestamp: currentMomentWithTimezone.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
            local_timezone: truncateLongText(moment.tz.guess(), 100),
            utc_timestamp: new Date().toUTCString(),
            downtime_method_capture_id:
                store.machineCenter?.downtime_method_capture_id ?? machineCenter?.downtime_method_capture_id,
            machine_status: uptime ? 1 : 0,
            current_failure_details_step: failureDetailsStep,
            last_app_update_date: buildDateTimeStamp,
            last_data_update_date: store.lastUpdated ?? new Date(),
            last_internet_connection_recovery_date: internetConnectionRecoveryDate ?? lastInternetConnection,
            ...{ trigger: truncateLongText(trigger!, 100) },
            ...{ message: message },
            app_release_version: truncateLongText(process.env.REACT_APP_VERSION ?? "0.0.0", 100),
            operator_id: currentOperator ?? null,
            ...{ operative_system: truncateLongText(deviceOperativeSystem!, 100) },
            ...{ operative_system_version: truncateLongText(navigator.appVersion, 255) },
            ...{ device_type: truncateLongText(deviceType, 100) },
            //Todo: find a free library that retrieves this information, as javascript per se has no way of knowing the device model
            ...{ device_model: null },
        };

        try {
            const response = await insertHMILog({
                variables: { machineCenterLog: machineCenterLogInfo },
            });

            if (response.data?.insert_machine_center_logs_one?.message) {
                return JSON.parse(response.data.insert_machine_center_logs_one.message);
            }
        } catch (error) {
            console.log("error", error);
            return;
        }
    };

    const providerValue = {
        triggerMachineCenterLogs,
    };

    return <MachineCenterLogsContext.Provider value={providerValue}>{children}</MachineCenterLogsContext.Provider>;
};
